import {
  sessionListSchema,
  sessionResponseSchema,
  emailTwoFactorStartResponseSchema,
  twoFactorBackupCodesSchema,
  twoFactorSetupResponseSchema,
  type AuthenticatedUser,
  type PasswordChangeInput,
  type ProfileUpdateInput,
  type SessionEntry,
  type TwoFactorDisableInput,
  type TwoFactorSetupResponse,
  type EmailTwoFactorStartResponse,
  tenantTwoFactorPolicySchema,
  type TenantTwoFactorPolicy,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { sessionQueryKey } from '@/features/auth';
import { apiFetch, apiSend, type ApiError } from '@/lib/api-client';

export const securityKeys = {
  sessions: ['security', 'sessions'] as const,
  policy: ['security', 'two-factor-policy'] as const,
};

export function useTenantTwoFactorPolicy(): UseQueryResult<TenantTwoFactorPolicy, ApiError> {
  return useQuery({
    queryKey: securityKeys.policy,
    queryFn: () => apiFetch('/api/v1/auth/2fa/policy', tenantTwoFactorPolicySchema),
  });
}

export function useUpdateTenantTwoFactorPolicy(): UseMutationResult<
  TenantTwoFactorPolicy,
  ApiError,
  Partial<TenantTwoFactorPolicy>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch('/api/v1/auth/2fa/policy', tenantTwoFactorPolicySchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: (policy) => queryClient.setQueryData(securityKeys.policy, policy),
  });
}

export function useDisableEmailTwoFactor(): UseMutationResult<
  void,
  ApiError,
  { password: string; secondFactor: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiSend('/api/v1/auth/2fa/email/disable', { method: 'POST', body: input }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sessionQueryKey }),
  });
}

export function useStartEmailTwoFactor(): UseMutationResult<
  EmailTwoFactorStartResponse,
  ApiError,
  string
> {
  return useMutation({
    mutationFn: (password: string) =>
      apiFetch('/api/v1/auth/2fa/email/start', emailTwoFactorStartResponseSchema, {
        method: 'POST',
        body: { password },
      }),
  });
}

export function useConfirmEmailTwoFactor(): UseMutationResult<
  string[],
  ApiError,
  { challengeId: string; code: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const response = await apiFetch(
        '/api/v1/auth/2fa/email/confirm',
        twoFactorBackupCodesSchema,
        { method: 'POST', body: input },
      );
      return response.backupCodes;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sessionQueryKey }),
  });
}

export function useSetPrimaryTwoFactorMethod(): UseMutationResult<
  void,
  ApiError,
  'TOTP' | 'EMAIL'
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (method) =>
      apiSend('/api/v1/auth/2fa/primary', { method: 'PUT', body: { method } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sessionQueryKey }),
  });
}

export function useSessions(): UseQueryResult<SessionEntry[], ApiError> {
  return useQuery({
    queryKey: securityKeys.sessions,
    queryFn: async () => {
      const response = await apiFetch('/api/v1/auth/sessions', sessionListSchema);
      return response.data;
    },
  });
}

export function useRevokeSession(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiSend(`/api/v1/auth/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: securityKeys.sessions });
    },
  });
}

export function useRevokeOtherSessions(): UseMutationResult<void, ApiError, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiSend('/api/v1/auth/sessions/revoke-others', { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: securityKeys.sessions });
    },
  });
}

export function useChangePassword(): UseMutationResult<void, ApiError, PasswordChangeInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: PasswordChangeInput) =>
      apiSend('/api/v1/auth/password', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      void queryClient.invalidateQueries({ queryKey: securityKeys.sessions });
    },
  });
}

export function useUpdateProfile(): UseMutationResult<
  AuthenticatedUser,
  ApiError,
  ProfileUpdateInput
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ProfileUpdateInput) => {
      const response = await apiFetch('/api/v1/auth/me', sessionResponseSchema, {
        method: 'PATCH',
        body: input,
      });
      return response.user;
    },
    onSuccess: (user) => {
      queryClient.setQueryData(sessionQueryKey, user);
    },
  });
}

export function useStartTwoFactorSetup(): UseMutationResult<
  TwoFactorSetupResponse,
  ApiError,
  void
> {
  return useMutation({
    mutationFn: () =>
      apiFetch('/api/v1/auth/2fa/setup', twoFactorSetupResponseSchema, { method: 'POST' }),
  });
}

export function useEnableTwoFactor(): UseMutationResult<string[], ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (code: string) => {
      const response = await apiFetch('/api/v1/auth/2fa/enable', twoFactorBackupCodesSchema, {
        method: 'POST',
        body: { code },
      });
      return response.backupCodes;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

export function useRegenerateBackupCodes(): UseMutationResult<string[], ApiError, string> {
  return useMutation({
    mutationFn: async (code: string) => {
      const response = await apiFetch('/api/v1/auth/2fa/backup-codes', twoFactorBackupCodesSchema, {
        method: 'POST',
        body: { code },
      });
      return response.backupCodes;
    },
  });
}

export function useDisableTwoFactor(): UseMutationResult<void, ApiError, TwoFactorDisableInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: TwoFactorDisableInput) =>
      apiSend('/api/v1/auth/2fa/disable', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}
